import type { CallStatus } from "@bundle/core";
import { Icon, IconButton, Pressable, Stack, Text } from "../../primitives/index.ts";
import { useT } from "../../i18n/I18nProvider.tsx";

export type HuddlePillProps = {
  title: string;
  isDm: boolean;
  status: CallStatus;
  count: number;
  /** Someone (anyone) is speaking right now. */
  speaking: boolean;
  muted: boolean;
  onOpen: () => void;
  onMute: () => void;
  onLeave: () => void;
  testID: string;
};

/** Compact huddle indicator for the title bar: which conversation, how many, speaking, mute and leave. */
export function HuddlePill(p: HuddlePillProps) {
  const t = useT();
  const connecting = p.status === "joining" || p.status === "connecting" || p.status === "reconnecting";
  return (
    <Stack direction="horizontal" align="center" gap="1" paddingX="1" radius="full" background="bg.elevated" border={p.speaking ? "status.success" : "border.subtle"} testID={p.testID}>
      <Pressable direction="horizontal" radius="full" onPress={p.onOpen} accessibilityLabel={t("huddle.openWindow")} testID={`${p.testID}-open`}>
        <Stack direction="horizontal" align="center" gap="1.5" paddingX="1.5" paddingY="0.5">
          <Icon name="huddle" size="icon.sm" color={connecting ? "text.tertiary" : "status.success"} />
          <Text variant="caption" weight="semibold" lines={1}>
            {p.isDm ? p.title : `# ${p.title}`}
          </Text>
          <Text variant="caption" color="text.secondary">
            {String(p.count)}
          </Text>
        </Stack>
      </Pressable>
      <IconButton
        icon={p.muted ? "micOff" : "mic"}
        variant={p.muted ? "filled" : "ghost"}
        size="sm"
        accessibilityLabel={p.muted ? t("huddle.unmute") : t("huddle.mute")}
        onPress={p.onMute}
        testID={`${p.testID}-mute`}
      />
      <IconButton icon="leave" size="sm" accessibilityLabel={t("huddle.leave")} onPress={p.onLeave} testID={`${p.testID}-leave`} />
    </Stack>
  );
}
